import { motion } from "framer-motion";
import {
  Laptop, Monitor, Smartphone, HardDrive, Wrench, Cpu, Apple, Tablet,
  AlertOctagon, AlertTriangle, Zap, ArrowRight, MessageSquare, Phone, Clock, User,
  Star, ClipboardCheck, ClipboardList, Mail, ShieldAlert, Hourglass, Timer
} from "lucide-react";

const ICON_MAP = {
  laptop: Laptop, desktop: Monitor, phone: Smartphone,
  storage: HardDrive, other: Wrench, tablet: Tablet,
};

function getIcon(type, name) {
  if (type === "apple") {
    const n = (name || "").toLowerCase();
    if (n.includes("iphone")) return Smartphone;
    if (n.includes("ipad")) return Tablet;
    if (n.includes("imac") || n.includes("mac mini") || n.includes("mac pro")) return Monitor;
    return Apple;
  }
  return ICON_MAP[type] || Cpu;
}

function getDeviceLabel(type) {
  const labels = { apple: "Apple", laptop: "Laptop", desktop: "Desktop", phone: "Phone", storage: "Storage", other: "Hardware", tablet: "Tablet" };
  return labels[type] || "Device";
}

const C = {
  red: "#ff5c5c", yellow: "#facc15", cyan: "#7dd3fc", neutral: "#cbd5e1",
};

const BORDER = {
  red: "border-l-[#ff5c5c]",
  yellow: "border-l-[#facc15]",
  green: "border-l-emerald-500",
  pickup: "border-l-violet-400",
};

function alertColor(ticket) {
  return (ticket.is_critical || ticket.priority_color === "red") ? C.red : C.yellow;
}

function AlertIconFor(ticket) {
  return (ticket.is_critical || ticket.priority_color === "red") ? AlertOctagon : AlertTriangle;
}

// Color the Total Age number by business-day age:
//   >=5 biz days -> red, >=3 biz days -> yellow, else neutral
function totalAgeColor(ticket) {
  const d = ticket.total_age_days || 0;
  if (d >= 5) return C.red;
  if (d >= 3) return C.yellow;
  return C.neutral;
}

// Color the status-age number based on per-status thresholds (matches backend
// SLA): Received >2d red / >1.5d yellow, Being Diagnosed >48h red / >36h
// yellow, In Progress >72h red / >54h yellow. For other statuses we fall back
// to the overall priority color.
function statusAgeColor(ticket) {
  const h = ticket.time_in_stage_hours || 0;
  const d = ticket.status_age_days || h / 9;
  const s = ticket.status;
  if (s === "Received") {
    if (d > 2) return C.red;
    if (d > 1.5) return C.yellow;
  } else if (s === "Being Diagnosed") {
    if (h > 48) return C.red;
    if (h > 36) return C.yellow;
  } else if (s === "In Progress") {
    if (h > 72) return C.red;
    if (h > 54) return C.yellow;
  }
  if (ticket.priority_color === "red") return C.red;
  if (ticket.priority_color === "yellow") return C.yellow;
  return C.neutral;
}

// Hide alert lines that just say "ticket is old" — the Total Age timer at the
// top of the card already conveys that. Keep status-specific overdue/SOS
// alerts and external signals (SMS/EMAIL/PARTS/UNASSIGNED/etc.).
const REDUNDANT_AGE_ALERTS = new Set([
  "AGING",
  "AGING - NEEDS ESCALATION",
]);
function filterAlerts(alerts) {
  return alerts.filter(a => a && !REDUNDANT_AGE_ALERTS.has(a.trim().toUpperCase()));
}

/**
 * Unified ticket card — uses SOS-size styling for ALL contexts.
 * Only visual difference: SOS strip cards get pulse animation.
 */
export function TicketCard({ ticket, variant = "column", dimmed = false }) {
  const DevIcon = getIcon(ticket.device_type, ticket.device_name);
  const border = BORDER[ticket.priority_color] || "border-l-slate-600";
  const isSOS = variant === "sos";
  const deviceLabel = getDeviceLabel(ticket.device_type);
  const alerts = filterAlerts(ticket.alert_message ? ticket.alert_message.split(" | ") : []);
  const aColor = alertColor(ticket);
  const AIcon = AlertIconFor(ticket);
  const totalAgeStr = ticket.total_age_display || ticket.total_age_days != null ? (ticket.total_age_display || `${ticket.total_age_days}d`) : "—";
  const statusAgeStr = ticket.status_age_display || ticket.time_in_stage || "—";
  const tColor = totalAgeColor(ticket);
  const sColor = statusAgeColor(ticket);

  return (
    <motion.div
      layoutId={ticket.id}
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: dimmed ? 0.55 : 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96 }}
      transition={{ type: "spring", stiffness: 350, damping: 30 }}
      data-testid={`ticket-card-${ticket.ticket_number}`}
      className={`
        relative bg-[#0F172A] border border-white/[0.07] rounded-md
        border-l-[3px] ${dimmed ? "border-l-slate-600" : border}
        ${isSOS ? "sos-pulse sos-border-pulse min-w-[260px]" : "w-full h-full"}
        p-3 overflow-hidden flex flex-col
        ${!dimmed && ticket.priority_color === "red" && !isSOS ? "glow-red" : ""}
        ${!dimmed && ticket.priority_color === "yellow" && !isSOS ? "glow-yellow" : ""}
      `}
    >
      {/* ── Top-right indicators ─────────────── */}
      <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
        {ticket.escalation_risk === "high" && (
          <ShieldAlert data-testid={`escalation-${ticket.ticket_number}`} className="w-4 h-4" style={{ color: "#ef4444" }} strokeWidth={2.5} />
        )}
        {ticket.escalation_risk === "elevated" && (
          <ShieldAlert data-testid={`escalation-${ticket.ticket_number}`} className="w-4 h-4" style={{ color: "#f59e0b" }} strokeWidth={2} />
        )}
        {ticket.recommended_next && (
          <Star data-testid={`recommended-${ticket.ticket_number}`} className="w-4 h-4" style={{ color: "#38bdf8", fill: "#38bdf8" }} strokeWidth={2} />
        )}
        {ticket.prelim_complete ? (
          <ClipboardCheck data-testid={`prelim-indicator-${ticket.ticket_number}`} className="w-4 h-4" style={{ color: "#34d399" }} strokeWidth={2.5} />
        ) : (
          <ClipboardList data-testid={`prelim-indicator-${ticket.ticket_number}`} className="w-4 h-4" style={{ color: "#f59e0b" }} strokeWidth={2} />
        )}
      </div>

      {/* ═══ HEADER ══════════════════════════════ */}
      <div className="shrink-0">
        {/* Badge + Name */}
        <div className="flex items-center gap-2 min-w-0 pr-14">
          <span data-testid={`ticket-badge-${ticket.ticket_number}`} className="shrink-0 font-bold text-[18px] px-1.5 py-0.5 rounded border leading-none text-white" style={{
            borderColor: ticket.priority_color === "red" ? `${C.red}99` :
                         ticket.priority_color === "yellow" ? `${C.yellow}80` :
                         ticket.priority_color === "pickup" ? "#a78bfa80" : "#34d39966",
            backgroundColor: ticket.priority_color === "red" ? `${C.red}18` :
                             ticket.priority_color === "yellow" ? `${C.yellow}14` :
                             ticket.priority_color === "pickup" ? "#8b5cf618" : "#34d39914",
            fontFamily: "'JetBrains Mono', monospace",
          }}>{ticket.ticket_number}</span>
          <span className="text-[15px] font-bold text-slate-50 truncate">{ticket.device_name}</span>
        </div>

        {/* Device + Repair + Days + Tech */}
        <div className="flex items-center justify-between mt-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <div className="flex items-center gap-0.5 shrink-0 px-1 py-0.5 rounded bg-slate-800/60">
              <DevIcon className="w-3.5 h-3.5" style={{ color: C.neutral }} strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-wide" style={{ color: "#94a3b8" }}>{deviceLabel}</span>
            </div>
            <span className="text-[13px] truncate" style={{ color: C.neutral }}>{ticket.repair_type}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0 ml-2">
            {ticket.shop_days > 0 && (
              <span data-testid={`shop-days-${ticket.ticket_number}`} className="flex items-center gap-0.5 text-[11px] font-bold px-1 py-0.5 rounded" style={{
                color: ticket.shop_days >= 30 ? C.red : ticket.shop_days >= 14 ? C.yellow : "#94a3b8",
                backgroundColor: ticket.shop_days >= 30 ? `${C.red}18` : ticket.shop_days >= 14 ? `${C.yellow}14` : "#1e293b99",
              }}>
                <Clock className="w-3 h-3" strokeWidth={2} />{ticket.shop_days}d
              </span>
            )}
            <span className="text-[13px] font-medium" style={{ color: "#94a3b8" }}>{ticket.assigned_technician}</span>
          </div>
        </div>

        {/* Status + Waiting badge (timer moved to dedicated panel below) */}
        <div className="flex items-center justify-between mt-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[15px] font-semibold truncate" style={{ color: C.neutral }}>{ticket.status}</span>
            {ticket.waiting_on === "shop" && (
              <span data-testid={`waiting-on-${ticket.ticket_number}`} className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded" style={{
                color: "#fbbf24", backgroundColor: "#fbbf2418", border: "1px solid #fbbf2440"
              }}>On Us</span>
            )}
            {ticket.waiting_on === "customer" && (
              <span data-testid={`waiting-on-${ticket.ticket_number}`} className="shrink-0 text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded" style={{
                color: "#94a3b8", backgroundColor: "#94a3b814", border: "1px solid #94a3b830"
              }}>On Cust</span>
            )}
          </div>
        </div>

        {/* ── PROMINENT TWO-TIMER PANEL ────────────────────
            Total Age = biggest number on the card.
            "In <Status>: Yd Zh" = second prominent line. */}
        <div
          data-testid={`timers-${ticket.ticket_number}`}
          className="mt-1.5 px-2 py-1.5 rounded-md border bg-slate-900/60"
          style={{
            borderColor: tColor === C.red ? `${C.red}55`
                       : tColor === C.yellow ? `${C.yellow}55`
                       : "#1e293b",
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <Timer className="w-3.5 h-3.5 shrink-0" style={{ color: tColor }} strokeWidth={2.5} />
              <span className="text-[9px] font-bold uppercase tracking-wider shrink-0" style={{ color: "#94a3b8" }}>Total Age</span>
            </div>
            <span
              data-testid={`total-age-${ticket.ticket_number}`}
              className={`font-display font-extrabold tracking-tight shrink-0 ${
                tColor === C.red ? "timer-warn" : ""
              }`}
              style={{
                color: tColor,
                fontSize: isSOS ? "28px" : "26px",
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {totalAgeStr}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2 mt-1 min-w-0">
            <span className="text-[11px] font-medium truncate" style={{ color: "#94a3b8" }}>
              {(/^in\b/i).test(ticket.status || "") ? ticket.status : `In ${ticket.status}`}
            </span>
            <span
              data-testid={`status-age-${ticket.ticket_number}`}
              className="font-display font-bold tracking-tight shrink-0"
              style={{
                color: sColor,
                fontSize: "15px",
                lineHeight: 1,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {statusAgeStr}
            </span>
          </div>
        </div>
      </div>

      {/* ═══ MIDDLE — grows to fill space ═════════ */}
      <div className="flex-1 flex flex-col justify-center gap-0.5 min-h-0 overflow-hidden mt-1">
        {/* Alerts — all visible */}
        {alerts.length > 0 && (
          <div className="flex flex-col gap-0.5" data-testid={`alert-${ticket.ticket_number}`}>
            {alerts.map((alert, i) => (
              <div key={i} className="flex items-center gap-1.5 min-w-0">
                <AIcon className={`w-5 h-5 shrink-0 ${ticket.is_critical && i === 0 ? "blink" : ""}`} style={{ color: aColor }} strokeWidth={2.5} />
                <span className="text-[14px] font-extrabold truncate" style={{ color: aColor }}>{alert}</span>
              </div>
            ))}
          </div>
        )}

        {/* SMS subline */}
        {ticket.sms_subline && (
          <div data-testid={`sms-subline-${ticket.ticket_number}`} className="flex items-center gap-1.5 min-w-0">
            <MessageSquare className="w-4 h-4 shrink-0" style={{ color: C.red }} strokeWidth={2} />
            <span className="text-[13px] font-bold truncate" style={{ color: C.red }}>{ticket.sms_subline}</span>
          </div>
        )}

        {/* Email subline */}
        {ticket.email_subline && (
          <div data-testid={`email-subline-${ticket.ticket_number}`} className="flex items-center gap-1.5 min-w-0">
            <Mail className="w-4 h-4 shrink-0" style={{ color: "#f59e0b" }} strokeWidth={2} />
            <span className="text-[13px] font-bold truncate" style={{ color: "#f59e0b" }}>{ticket.email_subline}</span>
          </div>
        )}

        {/* Call subline */}
        {ticket.call_subline && (
          <div data-testid={`call-subline-${ticket.ticket_number}`} className="flex items-center gap-1.5 min-w-0">
            <Phone className="w-4 h-4 shrink-0" style={{ color: C.yellow }} strokeWidth={2} />
            <span className="text-[13px] font-bold truncate" style={{ color: C.yellow }}>{ticket.call_subline}</span>
          </div>
        )}

        {/* Escalation subline */}
        {ticket.escalation_subline && (
          <div data-testid={`escalation-subline-${ticket.ticket_number}`} className="flex items-center gap-1.5 min-w-0">
            <ShieldAlert className="w-4 h-4 shrink-0" style={{ color: ticket.escalation_risk === "high" ? "#ef4444" : "#f59e0b" }} strokeWidth={2} />
            <span className="text-[13px] font-bold truncate" style={{ color: ticket.escalation_risk === "high" ? "#ef4444" : "#f59e0b" }}>{ticket.escalation_subline}</span>
          </div>
        )}

        {/* Stale + Last update (merged) */}
        {(ticket.stale_flag || ticket.last_update_subline) && (
          <div data-testid={`stale-update-${ticket.ticket_number}`} className="flex items-center gap-1.5 min-w-0">
            {ticket.stale_flag ? (
              <Hourglass className="w-4 h-4 shrink-0" style={{ color: "#a78bfa" }} strokeWidth={2} />
            ) : (
              <User className="w-4 h-4 shrink-0" style={{ color: "#94a3b8" }} strokeWidth={2} />
            )}
            <span className="text-[13px] font-medium truncate" style={{ color: ticket.stale_flag ? "#a78bfa" : "#94a3b8" }}>
              {[ticket.stale_flag && ticket.stale_reason, ticket.last_update_subline].filter(Boolean).join(" · ")}
            </span>
          </div>
        )}

        {/* Shelf aging for Received tickets */}
        {ticket.status === "Received" && ticket.shelf_biz_days != null && (
          <div data-testid={`shelf-aging-${ticket.ticket_number}`} className={`flex items-center gap-1.5 min-w-0 ${ticket.shelf_flagged ? "shelf-flag-pulse" : ""}`}>
            <Hourglass className="w-4 h-4 shrink-0" style={{ color: ticket.shelf_flagged ? "#ff5c5c" : "#38bdf8" }} strokeWidth={2.5} />
            <span className="text-[13px] font-extrabold truncate" style={{
              color: ticket.shelf_flagged ? "#ff5c5c" : "#38bdf8",
            }}>On Shelf: {ticket.shelf_biz_days} business days</span>
          </div>
        )}

        {/* Breach timer */}
        {ticket.breach_timer && (
          <div className="text-[13px] font-display font-bold blink tracking-wide" style={{ color: C.red }}>{ticket.breach_timer}</div>
        )}
      </div>

      {/* ═══ FOOTER — anchored to bottom ═════════ */}
      {ticket.next_action && (
        <div className="shrink-0 flex items-center gap-1.5 mt-1 pt-1 border-t border-white/[0.05]">
          <Zap className="w-4 h-4 shrink-0" style={{ color: C.cyan }} strokeWidth={2} />
          <span data-testid={`next-action-${ticket.ticket_number}`} className="text-[14px] font-semibold truncate" style={{ color: C.cyan }}>{ticket.next_action}</span>
        </div>
      )}
    </motion.div>
  );
}
